import { AlertTriangle, Eye, Send } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { usePostingConfig } from '@/features/company/queries'
import type { PostingConfig } from '@/features/company/api'

/**
 * Standard amber banner shown above any page whose data lands in the GL
 * (Manage Accruals, Journal Entries, View Statements, Depreciation,
 * Prepaid, Loan, Invoices, Bills). The banner only appears when at least
 * one entry is unposted under that source's manual queue AND the tenant
 * has the posting config for that source set to 'manual'. When the
 * tenant has flipped the source to 'auto-post' the banner disappears
 * because there is nothing for the user to do - the worker will post
 * the entries on the next cycle.
 *
 * Wording is fixed so every page reads the same:
 *
 *   "{N} unposted {label} · Reports may not reflect latest data."
 *   [View pending] [Post Now]
 *
 * Both actions are wired by the caller. "Post Now" should open a
 * <PostingSelectorModal> with checkboxes; "View pending" should open the
 * same modal in read-only mode (or just be a duplicate that doesn't
 * commit on submit). Keeping the two visually adjacent matches the
 * accountant's mental model: peek first, post when ready.
 */
export type PostingBannerSource = Exclude<keyof PostingConfig, 'updatedAt'>

export interface PostingBannerProps {
  count: number
  /** Plural-ready noun phrase, e.g. "accruals", "journal entries", "depreciation entries". */
  label: string
  /** Singular form for the n=1 case, defaults to label without trailing 's'. */
  singularLabel?: string
  onView: () => void
  onPostNow: () => void
  /** True while a Post Now mutation is running - disables the buttons. */
  busy?: boolean
  /**
   * Posting-config source key. When provided and the tenant has set this
   * source to 'auto', the banner is suppressed entirely so the manual
   * post UX doesn't appear when auto-post handles it. Omitting this
   * prop preserves the legacy "always show when count > 0" behavior.
   */
  source?: PostingBannerSource
}

export function PostingBanner({
  count,
  label,
  singularLabel,
  onView,
  onPostNow,
  busy,
  source,
}: PostingBannerProps) {
  // Why: hooks must be called unconditionally. Querying posting config
  // even when `source` isn't set is harmless - TanStack will dedupe the
  // call across the page and the result is just ignored below.
  const cfgQ = usePostingConfig()
  if (count <= 0) return null
  if (source) {
    const mode = cfgQ.data?.config?.[source]
    if (mode === 'auto') return null
  }
  const display = count === 1
    ? singularLabel ?? label.replace(/s$/, '')
    : label
  return (
    <Alert variant="default" className="border-warning bg-warning/10">
      <AlertTriangle className="size-4" />
      <AlertTitle>
        <strong>{count}</strong> unposted {display} · Reports may not reflect latest data.
      </AlertTitle>
      <AlertDescription className="mt-2 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={onView} disabled={busy}>
          <Eye className="size-4" /> View pending
        </Button>
        <Button size="sm" onClick={onPostNow} disabled={busy}>
          <Send className="size-4" /> Post Now
        </Button>
      </AlertDescription>
    </Alert>
  )
}
